import { addHours, subHours } from "date-fns";
import { FunctionComponent, memo, useMemo } from "react";
import { SpinnerUntil } from "@edgetier/components";

import MetricValuesChart from "components-for/anomalies/metric-values-chart";
import useMetricModelOutputs from "hooks-for/models/use-metric-model-outputs";

import { getMetricId, selectMetric } from "./anomaly-summary-metric-chart.utilities";
import { HOURS_AFTER_END, HOURS_BEFORE_START } from "./anomaly-summary-metric-chart.constants";
import { IProps } from "./anomaly-summary-metric-chart.types";
import "./anomaly-summary-metric-chart.scss";

/**
 * From a model and anomaly events, find the first new anomaly event's metric, and get it's output to graph.
 * @param props.anomalyEvents Array of anomaly events occuring as part of a metric anomaly.
 * @param props.endDateTime   When the anomaly ended (could be null if it's still occurring).
 * @param props.modelId       Model that detected the anomaly.
 * @param props.startDateTime When the anomaly started.
 * @returns                   Chart of metric values over time.
 */
const AnomalySummaryMetricChart: FunctionComponent<IProps> = ({
    anomalyEvents,
    endDateTime,
    modelId,
    startDateTime,
}) => {
    const metricId = useMemo(() => getMetricId(anomalyEvents), [anomalyEvents]);

    // The request requires an end date which may not be defined if the anomaly is still active. In that case just use
    // the current date. This is memoised so each render doesn't create a new date.
    const queryParameters = useMemo(
        () => ({
            endDateTime: addHours(endDateTime || new Date(), HOURS_AFTER_END),
            metricId,
            startDateTime: subHours(startDateTime, HOURS_BEFORE_START),
        }),
        [endDateTime, metricId, startDateTime]
    );

    const queryOptions = { retry: false, select: selectMetric };
    const { data: metricModelOutputs, isLoading } = useMetricModelOutputs(modelId, queryParameters, queryOptions);

    return (
        <SpinnerUntil data={metricModelOutputs} isReady={!isLoading}>
            {!Array.isArray(metricModelOutputs) ? (
                "Error"
            ) : (
                <MetricValuesChart
                    endDateTime={endDateTime ?? new Date()}
                    isPreview={true}
                    mouseCallbacks={{}}
                    metricModelOutputs={metricModelOutputs}
                    startDateTime={startDateTime}
                />
            )}
        </SpinnerUntil>
    );
};

export default memo(AnomalySummaryMetricChart);
